// Cookie-consent state shared between the banner, analytics, and any
// other code that needs to know whether the visitor has opted in to
// non-essential storage. Persisted in localStorage under a versioned
// key so we can invalidate old consents if the categories change.

export const CONSENT_VERSION = 1;
const STORAGE_KEY = 'bf-cookie-consent-v1';

export interface CookiePreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export interface StoredConsent {
  version: number;
  preferences: CookiePreferences;
  decidedAt: string;
}

export const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

export const getStoredConsent = (): StoredConsent | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveConsent = (preferences: CookiePreferences): StoredConsent => {
  const record: StoredConsent = {
    version: CONSENT_VERSION,
    preferences: { ...preferences, necessary: true },
    decidedAt: new Date().toISOString(),
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent<StoredConsent>('bf-consent-change', { detail: record }));
  applyGoogleConsentMode(record.preferences);
  return record;
};

export const clearConsent = () => {
  window.localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('bf-consent-change', { detail: null }));
};

export const OPEN_PREFERENCES_EVENT = 'bf-open-cookie-preferences';
export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
};

// Google Consent Mode v2: https://developers.google.com/tag-platform/security/guides/consent
// gtag is loaded globally from index.html. We gate it via consent updates
// rather than by conditionally loading the tag — safer and reversible.
export const applyGoogleConsentMode = (prefs: CookiePreferences) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    ad_storage: prefs.marketing ? 'granted' : 'denied',
    ad_user_data: prefs.marketing ? 'granted' : 'denied',
    ad_personalization: prefs.marketing ? 'granted' : 'denied',
    functionality_storage: prefs.functional ? 'granted' : 'denied',
    personalization_storage: prefs.functional ? 'granted' : 'denied',
    security_storage: 'granted',
  });
};

export const hasConsent = (category: keyof CookiePreferences): boolean => {
  const stored = getStoredConsent();
  if (!stored) return false;
  return Boolean(stored.preferences[category]);
};
